/**
 * In/out — Altas, Bajas e Inactivos de usuarios (IT) en una sola página con
 * tabs. Feedback People 31/08: vive en el grupo Devices y se llama "In/out"
 * para no confundirlo con el onboarding de People (Clappi). Cada tab conserva
 * su permiso original (subtab:users:onboarding / offboarding / inactive) —
 * la ruta no puede expresar "alguno de los tres", así que el gate vive acá.
 */

import { Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/SharedAuthContext';
import { UserOnboardingPage } from './UserOnboardingPage';
import { UserOffboardingPage } from './UserOffboardingPage';
import { InactiveUsersPage } from './InactiveUsersPage';

const TAB_PERMS = {
  altas: 'subtab:users:onboarding',
  bajas: 'subtab:users:offboarding',
  inactivos: 'subtab:users:inactive',
} as const;

type InOutTab = keyof typeof TAB_PERMS;

export function InOutPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const denied = (p: string) => (user?.deniedPermissions ?? []).includes(p);
  const allowed = (Object.keys(TAB_PERMS) as InOutTab[]).filter((k) => !denied(TAB_PERMS[k]));

  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab') as InOutTab | null;
  // Si el tab pedido está denegado (o no existe), caé al primero permitido
  const tab = rawTab && allowed.includes(rawTab) ? rawTab : allowed[0];

  if (allowed.length === 0) return <Navigate to="/unauthorized" replace />;

  const setTab = (next: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="space-y-4">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {allowed.includes('altas') && <TabsTrigger value="altas">{t('users.onboarding')}</TabsTrigger>}
          {allowed.includes('bajas') && <TabsTrigger value="bajas">{t('users.offboarding')}</TabsTrigger>}
          {allowed.includes('inactivos') && (
            <TabsTrigger value="inactivos">{t('users.inactive')}</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="altas" className="mt-4">
          <UserOnboardingPage />
        </TabsContent>
        <TabsContent value="bajas" className="mt-4">
          <UserOffboardingPage />
        </TabsContent>
        <TabsContent value="inactivos" className="mt-4">
          <InactiveUsersPage />
        </TabsContent>
      </Tabs>
    </div>
  );
}
